import { useErrorMessage, toast } from '@sentinel/web-core';
import { Toggle } from '@sentinel/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { NotificationPreference } from '@sentinel/shared';
import { endpoints } from '@/core/api/endpoints';
import { keys } from '@/core/query/query-keys';

const K = 'shell.user.email';

/** Which important notifications also reach one's inbox (ADR 0019), one switch per kind, saved
 *  at once. The choice is the person's, the same in every organization. Shown in the user card. */
export function EmailPreferences() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const preferences = useQuery({
    queryKey: keys.notificationPreferences,
    queryFn: endpoints.notifications.preferences,
  });
  const save = useMutation({
    mutationFn: (item: NotificationPreference) =>
      endpoints.notifications.savePreferences({ items: [item] }),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.notificationPreferences, data);
      toast('ok', t(`${K}.saved`));
    },
    onError: (error) => toast('att', errorMessage(error)),
  });
  const items = preferences.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <div className="uc-sec">
      <p className="mlabel">{t(`${K}.title`)}</p>
      <p className="note">{t(`${K}.hint`)}</p>
      {items.map((item) => (
        <Toggle
          key={item.kind}
          pressed={item.email}
          disabled={save.isPending}
          label={t(`${K}.kinds.${item.kind}`)}
          onToggle={(email) => save.mutate({ kind: item.kind, email })}
        />
      ))}
    </div>
  );
}
